// Drawer «Asignar tiendas · {plantilla}» (handoff §5): Seg «Toda la marca
// / Tiendas específicas» + StorePicker con el diff contra lo persistido
// (originalIds). Copia el patrón de mantenciones/AsignarTiendasDrawer
// sobre /checklist-templates (ADR-0079):
//   - mismo alcance «tiendas», cambia la lista → PUT /:id/assets
//   - marca → tiendas → PATCH { scope: "assets", assetIds } (el backend
//     rechaza el cambio sin tiendas: CHECKLIST_TEMPLATE_SCOPE_EMPTY)
//   - tiendas → marca → PATCH { scope: "brand" }
// El plan lo decide `assignmentPlan` (model.ts); aquí sólo se ejecuta.
// Las candidatas vienen filtradas por la marca de la plantilla, así el
// CHECKLIST_TEMPLATE_BRAND_MISMATCH casi no se puede producir desde aquí;
// si llega igual (la tienda cambió de marca entre medio) se muestra
// inline. Con «Toda la marca» elegido no se pinta nada bajo el Seg salvo
// el hint: el prototipo no define otro contenido ahí.

import { useEffect, useId, useMemo, useRef, useState } from "react";
import type {
  ChecklistTemplateAdminSummary,
  ChecklistTemplateUpdateBody,
} from "@bitacorapro/api-types";
import { Drawer } from "../../../components/Drawer";
import { PageError } from "../../../components/PageError";
import { PageSkeleton } from "../../../components/PageSkeleton";
import { Seg } from "../../../components/Seg";
import { StorePicker, type StorePickerItem } from "../../../components/StorePicker";
import { StorePickerSkel } from "../../../components/skeleton-compositions/StorePickerSkel";
import { ApiError } from "../../../lib/api/error";
import { useQueryView } from "../../../lib/loading/useQueryView";
import {
  assignErrorMessage,
  assignmentPlan,
  pluralize,
  type ChecklistWords,
  type TemplateScope,
} from "./model";
import {
  useChecklistAssignedAssets,
  usePatchChecklistTemplate,
  usePutChecklistAssets,
} from "./queries";

// checklistTemplateUpdateBody.assetIds es un AssetId[] con marca de tipo
// (core-engine); aquí todo son strings y sólo el PATCH lo necesita.
type BrandedAssetIds = NonNullable<ChecklistTemplateUpdateBody["assetIds"]>;

export type AsignarTiendasDrawerProps = {
  template: ChecklistTemplateAdminSummary;
  words: ChecklistWords;
  /** Candidatas, ya filtradas a la marca de la plantilla (todas si no tiene). */
  stores: StorePickerItem[];
  storesLoading: boolean;
  /** El esqueleto de la lista pinta sólo pasado el retardo compartido. */
  storesSkelVisible: boolean;
  /** Reintentar de la carga de candidatas cuando falló; null si no falló.
   *  Sin candidatas no hay lista que elegir: se pinta el error, no un
   *  picker vacío. */
  storesRetry: (() => void) | null;
  /** Cuántas candidatas tiene la marca; null mientras no se sabe (carga o
   *  error), y entonces el toast de «Toda la marca» no cuenta. */
  storeCount: number | null;
  onClose: () => void;
  onSaved: (message: string) => void;
};

export function AsignarTiendasDrawer({
  template,
  words,
  stores,
  storesLoading,
  storesSkelVisible,
  storesRetry,
  storeCount,
  onClose,
  onSaved,
}: AsignarTiendasDrawerProps) {
  const assetsQuery = useChecklistAssignedAssets(template.id);
  const assetsView = useQueryView(assetsQuery);
  const putAssets = usePutChecklistAssets();
  const patch = usePatchChecklistTemplate();

  const [scope, setScope] = useState<TemplateScope>(template.scope);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const seededRef = useRef(false);
  const scopeLabelId = useId();

  // La selección parte de lo persistido, una sola vez: un refetch de fondo
  // no pisa una edición en curso.
  useEffect(() => {
    if (seededRef.current || !assetsQuery.data) return;
    seededRef.current = true;
    setSelectedIds(assetsQuery.data.assets.map((a) => a.id));
  }, [assetsQuery.data]);

  const originalIds = useMemo(
    (): string[] => assetsQuery.data?.assets.map((a) => a.id) ?? [],
    [assetsQuery.data]
  );

  const plan = assignmentPlan({
    originalScope: template.scope,
    scope,
    originalIds,
    selectedIds,
  });
  const isSaving = putAssets.isPending || patch.isPending;
  const assetsReady = assetsView.kind === "ready";
  // Con «específicas» y las candidatas caídas no se guarda: lo marcado no
  // se ve y no se puede revisar. «Toda la marca» no las necesita.
  const storesFailed = scope === "assets" && storesRetry !== null;
  const canSave =
    assetsReady && !storesLoading && !storesFailed && !isSaving && plan.kind !== "none";

  function onScopeChange(next: TemplateScope) {
    // Al pasar de marca a tiendas nada queda marcado: la lista persistida
    // de una plantilla de marca es un resto que el gerente nunca confirmó.
    if (next === "assets" && scope === "brand") setSelectedIds([]);
    setScope(next);
  }

  async function save() {
    if (plan.kind === "none") return;
    setError(null);
    try {
      if (plan.kind === "put") {
        await putAssets.mutateAsync({ templateId: template.id, assetIds: plan.assetIds });
      } else if (plan.body.scope === "assets") {
        await patch.mutateAsync({
          templateId: template.id,
          body: { scope: "assets", assetIds: plan.body.assetIds as BrandedAssetIds },
        });
      } else {
        await patch.mutateAsync({ templateId: template.id, body: { scope: "brand" } });
      }
      // Con «Toda la marca» el número es el de las candidatas: todas las
      // de la marca (o de la organización) reciben el checklist. Si no se
      // sabe cuántas son (la carga falló) se guarda igual y no se cuenta.
      const n = scope === "assets" ? selectedIds.length : storeCount;
      onSaved(
        n === null
          ? "Asignación guardada"
          : `Asignación guardada · ${pluralize(n, words.assetLower, words.assetPluralLower)}`
      );
      onClose();
    } catch (err) {
      setError(assignErrorMessage(err instanceof ApiError ? err.code : null, words));
    }
  }

  return (
    <Drawer
      title={`Asignar ${words.assetPluralLower} · ${template.label}`}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn btn--ghost"
            title="Cerrar sin cambiar la asignación"
            disabled={isSaving}
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn--primary"
            title={`Guardar en qué ${words.assetPluralLower} se usa este checklist`}
            disabled={!canSave}
            onClick={() => void save()}
          >
            {isSaving ? "Guardando…" : "Guardar asignación"}
          </button>
        </>
      }
    >
      {assetsView.kind === "error" ? (
        // Sin la lista persistida no hay diff posible (kept / added /
        // removed): una sección del drawer, el error no mueve el foco.
        <PageError
          title={`No pudimos cargar las ${words.assetPluralLower} de este checklist.`}
          description="Revisa tu conexión e intenta de nuevo. Si el problema sigue, avísanos."
          onRetry={assetsView.retry}
          autoFocus={false}
        />
      ) : (
        <>
          <div className="field ag-field">
            <span className="field__label" id={scopeLabelId}>
              Alcance
            </span>
            {/* Bloqueado hasta tener la lista persistida: cambiar el alcance
                antes dejaría que la siembra pisara el vaciado de
                onScopeChange con el resto que nunca debe mostrarse. */}
            <Seg
              labelledBy={scopeLabelId}
              value={scope}
              onChange={onScopeChange}
              disabled={isSaving || !assetsReady}
              options={[
                {
                  value: "brand",
                  label: "Toda la marca",
                  title: `Usar este checklist en todas las ${words.assetPluralLower} de la marca`,
                },
                {
                  value: "assets",
                  label: `${words.assetPlural} específicas`,
                  title: `Elegir ${words.assetPluralLower} una a una`,
                },
              ]}
            />
          </div>

          {error && (
            <p className="auth-error" role="alert">
              {error}
            </p>
          )}

          {scope === "assets" && storesRetry !== null ? (
            // Las candidatas no llegaron: sin lista no hay nada que elegir.
            // Una sección del drawer, el error no mueve el foco.
            <PageError
              title={`No pudimos cargar las ${words.assetPluralLower} disponibles.`}
              description="Revisa tu conexión e intenta de nuevo. Si el problema sigue, avísanos."
              onRetry={storesRetry}
              autoFocus={false}
            />
          ) : (
            scope === "assets" &&
            (storesLoading || assetsView.kind === "loading" ? (
              <PageSkeleton
                label={`Cargando ${words.assetPluralLower}…`}
                visible={storesSkelVisible || (assetsView.kind === "loading" && assetsView.visible)}
                announce={false}
                kind="section"
              >
                <StorePickerSkel />
              </PageSkeleton>
            ) : (
              <StorePicker
                stores={stores}
                selectedIds={selectedIds}
                onChange={setSelectedIds}
                disabled={isSaving}
                originalIds={originalIds}
                legend={`${words.assetPlural} que usan esta plantilla`}
                addedLabel="Se agrega"
                removedLabel="Se quita"
              />
            ))
          )}

          <p className="field__hint ag-mt8">
            Las {words.assetPluralLower} que quites dejan de recibir este checklist desde la próxima
            fecha; lo ya registrado se conserva.
          </p>
        </>
      )}
    </Drawer>
  );
}
